/**
 * Portal (ÜberdachungPreis24): Tiefe und Bewegung, ohne dass eine Seite davon abhaengt.
 *
 *  - 3D-Dach auf der Startseite: folgt den Massen und der Eindeckung aus dem Formular.
 *    three.js wird erst geladen, wenn die Buehne fast im Bild ist, nur auf Geraeten mit
 *    WebGL und ohne Datensparmodus; der Platz bleibt mit dem Standbild belegt.
 *  - Leichte Neigung der Karten unter dem Zeiger (nur Maus, nicht bei reduzierter Bewegung).
 *
 * Seitenwechsel und Einblenden beim Scrollen laufen rein ueber CSS (portal.css).
 */

const ruhig = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// --------------------------------------------------------------------------
// 3D-Dach
// --------------------------------------------------------------------------

/** Wartezeit nach der letzten Eingabe, bevor das Modell neu gebaut wird. */
const ENTPRELLEN_MS = 160;

function zahlMm(roh: string): number | null {
  const n = Number(roh.trim().replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 1000) : null;
}

function webglVerfuegbar(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") ?? c.getContext("webgl"));
  } catch {
    return false;
  }
}

function sparmodus(): boolean {
  const verbindung = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return verbindung?.saveData === true;
}

async function startePortalDach(buehne: HTMLElement): Promise<void> {
  const form = document.querySelector<HTMLFormElement>("[data-vg-projekt]");
  const modul = await import("./dach3d-held.ts");
  const felder = form ? Array.from(form.querySelectorAll<HTMLInputElement>("[data-vg-mass]")) : [];
  const dachWahl = (): string => {
    const gewaehlt = form?.querySelector<HTMLInputElement>('input[name="dach"]:checked')?.value;
    return gewaehlt === "poly" ? "pc16" : "vsg8";
  };
  const masse = (): [number, number] | null => {
    const [b, t] = felder.map((f) => zahlMm(f.value));
    return b && t ? [b, t] : null;
  };
  const start = masse();
  const steuerung = modul.starteHeldenBuehne(buehne, {
    transparent: true,
    ...(start ? { breiteMm: start[0], tiefeMm: start[1] } : {}),
    dach: dachWahl(),
  });
  buehne.classList.add("ist-3d");

  let timer = 0;
  const aktualisiere = (): void => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const m = masse();
      if (m) steuerung.setzeMasse(m[0], m[1], dachWahl());
    }, ENTPRELLEN_MS);
  };
  form?.addEventListener("input", aktualisiere);
  form?.addEventListener("change", aktualisiere);
}

function initDach(): void {
  const buehne = document.querySelector<HTMLElement>("[data-pt-buehne]");
  if (!buehne || sparmodus() || !webglVerfuegbar()) return;
  const laden = (): void => void startePortalDach(buehne).catch(() => buehne.classList.remove("ist-3d"));
  if (!("IntersectionObserver" in window)) {
    laden();
    return;
  }
  const beobachter = new IntersectionObserver(
    (eintraege) => {
      if (!eintraege.some((e) => e.isIntersecting)) return;
      beobachter.disconnect();
      // Nach dem ersten Bild: der Text ist das LCP-Element, nicht die Szene
      if ("requestIdleCallback" in window) window.requestIdleCallback(laden, { timeout: 1500 });
      else setTimeout(laden, 200);
    },
    { rootMargin: "200px" },
  );
  beobachter.observe(buehne);
}

// --------------------------------------------------------------------------
// Kartenneigung
// --------------------------------------------------------------------------

/** Maximale Neigung in Grad. */
const NEIGUNG_GRAD = 3.5;
const KARTEN = ".vg-gewerk, .vg-partner, .vg-so-liste li";

function initNeigung(): void {
  if (ruhig || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
  for (const karte of Array.from(document.querySelectorAll<HTMLElement>(KARTEN))) {
    karte.addEventListener("pointermove", (e) => {
      const r = karte.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      karte.style.setProperty("--pt-ry", `${(x * NEIGUNG_GRAD * 2).toFixed(2)}deg`);
      karte.style.setProperty("--pt-rx", `${(-y * NEIGUNG_GRAD * 2).toFixed(2)}deg`);
    });
    karte.addEventListener("pointerleave", () => {
      karte.style.removeProperty("--pt-ry");
      karte.style.removeProperty("--pt-rx");
    });
  }
}

initDach();
initNeigung();
